<template>
  <div class="flex flex-col w-full mx-auto mt-3 md:hidden">
    <div class="flex px-2 mb-3">
      <!-- Search Input -->
      <div class="w-full">
        <InputField v-model:keyword="keyword" />
      </div>
      <!-- Filters Button -->
      <div class="flex justify-end w-2/12 ml-2">
        <MoreFiltersButton @click="toggleModal" />
      </div>
    </div>

    <vue-horizontal responsive :button="false" ref="horizontal" class="px-2">
      <div class="flex items-center justify-between space-x-2">
        <!-- Dropdowns -->
        <button
          v-if="property_tenure_options"
          @click="showFilter('buy')"
          :class="tenureButtonClass"
          class="flex items-center justify-center w-auto px-4 h-[35px] text-sm border rounded-lg font-light"
        >
          {{ selectedTenureLabel || "Buy" }}
        </button>
        <button
          v-if="property_types"
          @click="showFilter('property-types')"
          :class="propertyTypeButtonClass"
          class="flex items-center justify-center w-auto px-4 h-[35px] text-sm border rounded-lg font-light"
        >
          {{ selectedPropertyTypeLabel || "Property Type" }}
        </button>
        <!-- Beds & Baths dropdown -->
        <button
          @click="showFilter('beds-baths')"
          :class="bedsBathsButtonClass"
          class="flex items-center justify-center w-auto px-4 h-[35px] text-sm border rounded-lg font-light"
        >
          {{ selectedBedsBathsLabel || "Beds & Baths" }}
        </button>

        <!-- Price dropdown -->
        <button
          @click="showFilter('price')"
          :class="priceButtonClass"
          class="flex items-center truncate justify-center w-auto px-4 h-[35px] text-sm border rounded-lg font-light"
        >
          {{ $truncate(selectedPriceLabel ?? 0, 10) || "Price" }}
        </button>

        <!-- Find Button -->
        <FindButton :loading="loading" @click="filterProperties" />
      </div>
    </vue-horizontal>

    <!-- Filter Popups -->
    <div
      v-if="activeFilter === 'buy'"
      class="fixed bottom-0 left-0 z-50 w-full h-64 p-4 overflow-y-scroll bg-white shadow-lg"
    >
      <FilterDropdown
        :items="property_tenure_options"
        label="Buy"
        unique-identifier="buy"
        @item-selected="handleMenuItemSelected"
      />
      <button
        @click="hideFilter"
        class="w-full mt-4 text-sm text-center text-primary"
      >
        Close
      </button>
    </div>

    <div
      v-if="activeFilter === 'property-types'"
      class="fixed bottom-0 left-0 z-50 w-full h-64 p-4 overflow-y-scroll bg-white shadow-lg"
    >
      <FilterDropdown
        :items="property_types"
        label="Property Type"
        unique-identifier="property-types"
        @item-selected="handleMenuItemSelected"
      />
      <button
        @click="hideFilter"
        class="w-full mt-4 text-sm text-center text-primary"
      >
        Close
      </button>
    </div>

    <div
      v-if="activeFilter === 'beds-baths'"
      class="fixed bottom-0 left-0 z-50 w-full h-64 p-4 overflow-y-scroll bg-white shadow-lg"
    >
      <DropdownBedBath
        radius="lg"
        height="[35px]"
        button-label="Beds & Baths"
        @bath-bed-selected="handleBathBedSelected"
      />
      <button
        @click="hideFilter"
        class="w-full mt-4 text-sm text-center text-primary"
      >
        Close
      </button>
    </div>

    <div
      v-if="activeFilter === 'price'"
      class="fixed bottom-0 left-0 z-50 w-full h-64 p-4 overflow-y-scroll bg-white shadow-lg"
    >
      <div class="p-4 bg-white rounded-lg-lg">
        <!-- Price Range Section -->
        <div class="flex items-center justify-between mb-4">
          <div class="w-auto pr-2 md:w-1/2">
            <input
              v-model="selectedMinPrice"
              type="text"
              class="w-32 h-12 px-2 py-1 mb-2 font-light text-gray-500 border rounded-lg md:w-full border-stone-400 placeholder:text-gray-500 focus:outline-none focus:border-stone-400"
              placeholder="Min Price (GHS)"
              @input="emitPriceRangeSelected"
            />
          </div>
          <div class="w-auto pl-2 md:w-1/2">
            <input
              v-model="selectedMaxPrice"
              type="text"
              class="w-32 h-12 px-2 py-1 mb-2 font-light text-gray-500 border rounded-lg md:w-full border-stone-400 placeholder:text-gray-500 focus:outline-none focus:border-stone-400"
              placeholder="Max Price (GHS)"
              @input="emitPriceRangeSelected"
            />
          </div>
        </div>

        <!-- Separator -->
        <hr
          class="my-4 border-t border-stone-200"
          v-if="selectedMinPrice || selectedMaxPrice"
        />

        <!-- Reset Button Section -->
        <div class="flex justify-start">
          <button
            v-if="selectedMinPrice || selectedMaxPrice"
            type="button"
            class="px-2 py-1 text-sm text-primary focus:outline-none hover:text-primary"
            @click="resetPrice"
          >
            Reset
          </button>
        </div>

        <button
          @click="hideFilter"
          class="w-full mt-4 text-sm text-center text-primary"
        >
          Close
        </button>
      </div>
    </div>
    <SearchFilterModal
      :show-modal="localShowModal"
      @close-modal="closeViewModal"
      @more-property-filters="handleMorePropertyFilters"
    />
  </div>
</template>

<script>
import { ref, watch, defineComponent, computed } from "vue";
import InputField from "./InputField.vue";
import FilterDropdown from "./FilterDropdown.vue";
import DropdownBedBath from "@/components/form-components/BedBathDropdown.vue";
import FindButton from "./FindButton.vue";
import MoreFiltersButton from "./MoreFiltersButton.vue";
import VueHorizontal from "vue-horizontal";
import SearchFilterModal from "@/features/properties/SearchFilterModal.vue";

export default defineComponent({
  name: "MobileView",
  components: {
    FilterDropdown,
    DropdownBedBath,
    FindButton,
    VueHorizontal,
    InputField,
    MoreFiltersButton,
    SearchFilterModal,
  },
  props: {
    property_tenure_options: Array,
    property_types: Array,
    showModal: Boolean,
  },
  emits: ["filter-parameters"],
  setup(props, { emit }) {
    const keyword = ref("");
    const activeFilter = ref(null);
    const selectedTenureLabel = ref(null);
    const selectedPropertyTypeLabel = ref(null);
    const selectedBedsBathsLabel = ref(null);
    const selectedPriceLabel = ref(null);
    const selectedMinPrice = ref(null);
    const selectedMaxPrice = ref(null);
    const localShowModal = ref(props.showModal);
    const propertyTypeId = ref(null);
    const tenureId = ref(null);
    const minPrice = ref(null);
    const maxPrice = ref(null);
    const numBed = ref(null);
    const numBath = ref(null);
    const loading = ref(false);

    const handleMenuItemSelected = (payload) => {
      if (payload.dropdownIdentifier === "property-types") {
        propertyTypeId.value = payload.itemId;
        selectedPropertyTypeLabel.value = payload.label;
      } else if (payload.dropdownIdentifier === "buy") {
        tenureId.value = payload.itemId;
        selectedTenureLabel.value = payload.label;
      }
      hideFilter();
    };

    const emitPriceRangeSelected = () => {
      emit("update:minPrice", selectedMinPrice.value);
      emit("update:maxPrice", selectedMaxPrice.value);
      selectedPriceLabel.value = `${selectedMinPrice.value || ""} - ${
        selectedMaxPrice.value || ""
      }`;
    };

    const handleBathBedSelected = (payload) => {
      numBed.value = payload.Bedroom.value;
      numBath.value = payload.Bathroom.value;
      selectedBedsBathsLabel.value = `${payload.Bedroom.value} Beds, ${payload.Bathroom.value} Baths`;
      hideFilter();
    };

    const showFilter = (filter) => {
      activeFilter.value = filter;
    };

    const hideFilter = () => {
      activeFilter.value = null;
    };

    const toggleModal = () => {
      localShowModal.value = !localShowModal.value;
      emit("toggle-modal");
    };

    const closeViewModal = () => {
      localShowModal.value = false;
      emit("close-modal");
    };

    const resetPrice = () => {
      selectedMinPrice.value = null;
      selectedMaxPrice.value = null;
      selectedPriceLabel.value = null;
      hideFilter();
    };

    watch(
      () => props.showModal,
      (newVal) => {
        localShowModal.value = newVal;
      }
    );
    const tenureButtonClass = computed(() =>
      selectedTenureLabel.value
        ? "bg-[#F7F6FB] font-light rounded-lg border-primary text-primary"
        : ""
    );
    const propertyTypeButtonClass = computed(() =>
      selectedPropertyTypeLabel.value
        ? "bg-[#F7F6FB] font-light rounded-lg border-primary text-primary"
        : ""
    );
    const bedsBathsButtonClass = computed(() =>
      selectedBedsBathsLabel.value
        ? "bg-[#F7F6FB] font-light rounded-lg border-primary text-primary"
        : ""
    );
    const priceButtonClass = computed(() =>
      selectedPriceLabel.value
        ? "bg-[#F7F6FB] font-light rounded-lg border-primary text-primary"
        : ""
    );

    const handleMorePropertyFilters = (filters) => {
      emit("more-property-filters", filters);
    };

    const filterProperties = async () => {
      loading.value = true;
      try {
        await new Promise((resolve) => {
          setTimeout(() => {
            emit("filter-parameters", {
              keyword: keyword.value,
              min_price: selectedMinPrice.value,
              max_price: selectedMaxPrice.value,
              number_of_beds: numBed.value,
              number_of_baths: numBath.value,
              property_type_id: propertyTypeId.value,
              property_tenure_option_id: tenureId.value,
            });
            resolve();
          }, 1000);
        });
      } catch (error) {
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      propertyTypeId,
      tenureId,
      minPrice,
      maxPrice,
      numBed,
      numBath,
      filterProperties,
      tenureButtonClass,
      propertyTypeButtonClass,
      bedsBathsButtonClass,
      priceButtonClass,
      keyword,
      activeFilter,
      selectedTenureLabel,
      selectedPropertyTypeLabel,
      selectedBedsBathsLabel,
      selectedPriceLabel,
      selectedMinPrice,
      selectedMaxPrice,
      localShowModal,
      handleMenuItemSelected,
      emitPriceRangeSelected,
      handleBathBedSelected,
      showFilter,
      hideFilter,
      toggleModal,
      closeViewModal,
      resetPrice,
      handleMorePropertyFilters,
    };
  },
});
</script>

<style scoped>
/* Add your styles here */
</style>
